/**
 * FileDropZone Component
 * Reusable drag-and-drop file upload component
 */

import type { DragEvent, ChangeEvent } from 'react';
import { useState, useRef } from 'react';
import { Upload, X, FileText, FileArchive, Check } from 'lucide-react';
import { Button } from './Button';

export interface FileDropZoneProps {
  label: string;
  acceptedTypes: string[];  // e.g., ['.csv', '.zip']
  maxSize: number;         // in bytes
  onFileSelect: (file: File) => void;
  onFileRemove?: () => void;
  selectedFile?: File | null;
  disabled?: boolean;
  className?: string;
}

export const FileDropZone = ({
  label,
  acceptedTypes,
  maxSize,
  onFileSelect,
  onFileRemove,
  selectedFile,
  disabled = false,
  className = '',
}: FileDropZoneProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      validateAndSelectFile(files[0]);
    }
  };

  const handleFileInput = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      validateAndSelectFile(e.target.files[0]);
    }
  };

  const validateAndSelectFile = (file: File) => {
    setError(null);

    // Check file size
    if (file.size > maxSize) {
      const maxMB = (maxSize / 1024 / 1024).toFixed(2);
      const actualMB = (file.size / 1024 / 1024).toFixed(2);
      setError(`File too large: ${actualMB} MB (maximum: ${maxMB} MB)`);
      return;
    }

    // Check file extension
    const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!acceptedTypes.includes(fileExt)) {
      setError(`Invalid file type. Accepted: ${acceptedTypes.join(', ')}`);
      return;
    }

    onFileSelect(file);
  };

  const handleClick = () => {
    if (!disabled && !selectedFile) {
      fileInputRef.current?.click();
    }
  };

  const handleRemove = () => {
    setError(null);
    if (onFileRemove) {
      onFileRemove();
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
  };

  const getFileIcon = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (ext === 'csv') return <FileText className="h-8 w-8 text-blue-500" />;
    if (ext === 'zip') return <FileArchive className="h-8 w-8 text-purple-500" />;
    return <FileText className="h-8 w-8 text-meta" />;
  };

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-body mb-2">{label}</label>

      {!selectedFile ? (
        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={handleClick}
          className={`
            relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all
            ${isDragging ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-strong bg-surface hover:border-meta hover:bg-surface-hover'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <Upload className="mx-auto h-12 w-12 text-meta mb-3" />
          <p className="text-sm text-body mb-1">
            {isDragging ? 'Drop file here' : 'Drag and drop file here, or click to select'}
          </p>
          <p className="text-xs text-meta">
            Accepted: {acceptedTypes.join(', ')} • Max size: {formatFileSize(maxSize)}
          </p>

          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedTypes.join(',')}
            onChange={handleFileInput}
            className="hidden"
            disabled={disabled}
          />
        </div>
      ) : (
        <div className="border-2 border-green-500 bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 flex-1 min-w-0">
              {getFileIcon(selectedFile.name)}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-heading truncate">{selectedFile.name}</p>
                <p className="text-xs text-meta">{formatFileSize(selectedFile.size)}</p>
              </div>
              <Check className="h-6 w-6 text-green-600 flex-shrink-0" />
            </div>
            {onFileRemove && (
              <Button
                type="button"
                variant="secondary"
                onClick={handleRemove}
                className="ml-3 flex-shrink-0"
                disabled={disabled}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      )}

      {error && (
        <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
};
